/**
 * US Eastern calendar days, and their bounds as UTC instants.
 *
 * WHY THIS EXISTS SEPARATELY FROM `nfl-daily-reveal`: the same arithmetic is needed by
 * anything that has to answer "which day did this happen on?" for a US product, not just NFL
 * reveal days. The leaderboards need it because a US night game kicks off after UTC midnight
 * — an MLB slate at 21:38 ET on 31 August is 01:38 UTC on 1 September — so bucketing by the
 * UTC date files it under the wrong day, the wrong month, and the wrong monthly leaderboard
 * (DKPLAT-796).
 *
 * `nfl-daily-reveal` now delegates here rather than carrying its own copy.
 *
 * No dependencies: `Intl` already knows the EDT/EST rules, so the bounds are found by
 * probing rather than by hardcoding an offset that breaks twice a year.
 */

/** US Eastern — the timezone DK Sportsbook and the US leagues define their days in. */
export const US_EASTERN_TIMEZONE = 'America/New_York';

/** Format an instant as `YYYY-MM-DD` in US Eastern (matches DK app game dates). */
export function toEtDateString(instant: Date): string {
  // 'en-CA' yields ISO-ordered YYYY-MM-DD, which sorts and compares as a string.
  return instant.toLocaleDateString('en-CA', { timeZone: US_EASTERN_TIMEZONE });
}

/**
 * The UTC instant at which an ET calendar day BEGINS.
 *
 * Returned as a half-open pair with the next day's start, so callers can compare with
 * `>= start && < end` and never depend on how many decimal places an "end of day" carries.
 * That precision is not academic: the leaderboard cache stored one boundary as
 * `23:59:59.999` and a SQL function stored the same boundary as `23:59:59`, and because the
 * lookup was an equality check the two never matched (DKPLAT-796).
 */
export function etDayStartUtc(dateEt: string): Date {
  const [year, month, day] = dateEt.split('-').map(Number);

  // Seed at EDT's offset (UTC-4) and walk an hour at a time until the ET calendar date of the
  // instant matches the day we were asked for. At most a couple of steps in either direction,
  // whichever side of a DST switch we are on.
  let ms = Date.UTC(year, month - 1, day, 4, 0, 0, 0);
  for (let i = 0; i < 6; i++) {
    const at = toEtDateString(new Date(ms));
    if (at === dateEt) {
      // Walk back to the first instant that is still this ET date.
      while (toEtDateString(new Date(ms - 3600_000)) === dateEt) ms -= 3600_000;
      break;
    }
    ms += at < dateEt ? 3600_000 : -3600_000;
  }
  return new Date(ms);
}

/**
 * Half-open UTC bounds for a whole ET calendar day: `[start, end)`.
 *
 * A DST day is 23 or 25 hours long, which is why the end is the next day's start rather than
 * start + 24h.
 */
export function etDayUtcRange(dateEt: string): { start: Date; end: Date } {
  const start = etDayStartUtc(dateEt);
  const [year, month, day] = dateEt.split('-').map(Number);
  const next = new Date(Date.UTC(year, month - 1, day + 1));
  return { start, end: etDayStartUtc(toEtDateStringFromUtcParts(next)) };
}

/** `YYYY-MM-DD`. */
const ET_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
/** `HH:mm` or `HH:mm:ss` — seconds are accepted and ignored. */
const ET_TIME_RE = /^(\d{1,2}):(\d{2})(?::\d{2})?$/;

/** ET wall-clock parts of an instant, ISO-ordered so they parse back numerically. */
const ET_PARTS_FORMAT = new Intl.DateTimeFormat('en-CA', {
  timeZone: US_EASTERN_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

/**
 * ET's UTC offset in minutes AT a given instant — `-240` under EDT, `-300` under EST.
 *
 * Found by reading the ET wall clock at that instant and treating it as if it were UTC: the
 * gap between that and the real instant IS the offset. Probing `Intl` this way means the
 * DST rules are never encoded here, only consulted.
 */
function etOffsetMinutes(instant: Date): number {
  const parts: Record<string, string> = {};
  for (const part of ET_PARTS_FORMAT.formatToParts(instant)) parts[part.type] = part.value;
  const wallAsUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second)
  );
  return Math.round((wallAsUtc - instant.getTime()) / 60_000);
}

/**
 * The UTC instant of a WALL-CLOCK time on an ET calendar day.
 *
 * WHY THIS IS NOT `etDayStartUtc(date) + hours`: that adds elapsed minutes to the day's
 * start, which is only the same thing on a 24-hour day. It is out by a full hour for every
 * wall clock at or after a DST transition — an hour LATE across the spring-forward Sunday
 * (12:00 EDT is 16:00Z; the sum gives 17:00Z) and an hour EARLY across the autumn
 * fall-back Sunday (23:59 EST is 04:59Z the next day; the sum gives 03:59Z). The spring
 * case happened to be masked by the end-of-day clamp below; the autumn case was silently
 * wrong. Both are real: `end_of_day` reveal deadlines land on both Sundays every season.
 *
 * So the offset is resolved AT THE CANDIDATE INSTANT rather than at the day's start: read
 * the wall clock as if it were UTC, subtract ET's offset there, then re-probe at the
 * resulting instant and correct once. The first guess is at most an hour out, so the second
 * probe is inside the right offset period and the second subtraction is exact.
 *
 * Two edge cases remain, both resolving EARLIER and therefore to the safer deadline:
 *
 * - **A wall clock that does not exist** (02:30 on the spring-forward Sunday) resolves to
 *   01:30 EST, before the skipped hour.
 * - **A wall clock that happens twice** (01:30 on the fall-back Sunday) resolves to the
 *   first of the two passes, 01:30 EDT.
 *
 * The clamp to the day's last instant is kept as a backstop: nothing should now overflow
 * the requested ET day, but a rule saying "the day closes at 23:59 ET" must never be able
 * to mean "00:59 tomorrow", which would leave a Daily Reveal day open past its own deadline.
 *
 * The offset is never hardcoded — `etOffsetMinutes` and `etDayUtcRange` both probe `Intl`.
 *
 * Returns null for a malformed date or time rather than an invalid Date, so a caller falls
 * back to its own rule instead of writing NaN into a timestamp column.
 */
export function etWallClockUtc(dateEt: string, timeEt: string): Date | null {
  if (!ET_DATE_RE.test(dateEt)) return null;

  const match = ET_TIME_RE.exec((timeEt ?? '').trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;

  const [year, month, day] = dateEt.split('-').map(Number);
  const wallAsUtc = Date.UTC(year, month - 1, day, hours, minutes, 0, 0);
  const firstGuess = wallAsUtc - etOffsetMinutes(new Date(wallAsUtc)) * 60_000;
  const candidate = wallAsUtc - etOffsetMinutes(new Date(firstGuess)) * 60_000;

  const { end } = etDayUtcRange(dateEt);
  const lastInstant = new Date(end.getTime() - 1000);
  return candidate > lastInstant.getTime() ? lastInstant : new Date(candidate);
}

/**
 * Half-open UTC bounds for a whole ET calendar month: `[start, end)`.
 *
 * `monthEt` is `YYYY-MM`. Used by the monthly leaderboard, where getting this wrong moves a
 * contest between months — the August total in DKPLAT-796 was missing its 31 August slate
 * because that slate's UTC date was 1 September.
 */
export function etMonthUtcRange(monthEt: string): { start: Date; end: Date } {
  const [year, month] = monthEt.split('-').map(Number);
  const firstOfNext = month === 12 ? `${year + 1}-01-01` : `${year}-${String(month + 1).padStart(2, '0')}-01`;
  return { start: etDayStartUtc(`${monthEt}-01`), end: etDayStartUtc(firstOfNext) };
}

/** `YYYY-MM-DD` from a Date's UTC parts — used only to name the day after a given one. */
function toEtDateStringFromUtcParts(date: Date): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
